/**
 * useCheckoutLinkOut - Trade Loop v2.0 (Card 18, tasks A, B and C)
 *
 * The native side of the paywall: is a purchase call to action allowed at all, and if so what
 * URL does it point at.
 *
 * ## Eligibility is the server's call, and an unreachable server is a no
 *
 * `allowed` starts false and only becomes true when the server says so. That default is about
 * REACHABILITY, not geography: if the eligibility call fails we also cannot mint a hand-off
 * token, so a CTA would lead nowhere anyway.
 *
 * The geography decision itself lives entirely on the server (see utils/request-region.ts) and
 * since 2026-08-11 it treats "no country signal" as allowed — Apple 3.1.1 keys on the App Store
 * storefront, which Card 11 restricts to the US, not on the IP the phone is using. Do not
 * re-derive any of that here; this hook only relays the answer.
 *
 * ## Why the token is minted during the disclosure, not on the final tap
 *
 * WKWebView only routes `window.open` to Capacitor's delegate — and therefore to the system
 * browser — for a **user-gesture-initiated** open. Awaiting a fetch first spends the gesture, so
 * by the time a token came back the open would be refused.
 *
 * The pre-link disclosure resolves this without a trick. Google's program requires telling the
 * user they are about to leave, so the flow is already two taps: tap one opens the disclosure and
 * starts the mint; tap two is a fresh gesture with the URL already in hand. `isPreparing` keeps
 * the second tap disabled until it is, so it can never fire on a half-ready state.
 *
 * The token is minted per link-out and never reused across them — it lands in browser history and
 * possibly on a clipboard, so `reset()` clears it as soon as the sheet closes.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { isNative, platform } from '@/utils/platform';

export type LinkOutPlan = 'pro' | 'grow';

interface Eligibility {
  /** May a purchase CTA be shown? False until the server confirms the US storefront. */
  allowed: boolean;
  /** Detected country, or null when the edge supplied no signal. Diagnostics only. */
  country: string | null;
  /** Which header answered, or 'none'. 'none' in production means the edge forwards no country. */
  source: string;
  /** Typeable fallback (gotradeloop.com/go). Carries no token, by design. */
  shortUrl: string;
  reason: 'outside_us_storefront' | 'region_unknown' | 'unavailable' | null;
}

const FAIL_CLOSED: Eligibility = {
  allowed: false,
  country: null,
  source: 'none',
  shortUrl: 'https://gotradeloop.com/go',
  reason: 'unavailable'
};

export function useCheckoutLinkOut() {
  const [eligibility, setEligibility] = useState<Eligibility>(FAIL_CLOSED);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(isNative);
  const [handoffUrl, setHandoffUrl] = useState<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);

  // Only native needs this. On the web the user is already in a browser with a session, so the
  // ordinary checkout redirect is both lawful and better, and neither store has any say over it.
  useEffect(() => {
    if (!isNative) {
      setIsCheckingEligibility(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await api.get('/api/workflows/subscriptions/link-out-eligibility');
        if (cancelled) return;

        if (response?.success && response.data) {
          setEligibility({
            allowed: Boolean(response.data.allowed),
            country: response.data.region?.country ?? null,
            source: response.data.region?.source ?? 'none',
            shortUrl: response.data.shortUrl || FAIL_CLOSED.shortUrl,
            reason: response.data.reason ?? null
          });
        } else {
          setEligibility(FAIL_CLOSED);
        }
      } catch (error) {
        // Fail closed. An unreachable eligibility check is not permission to show a CTA.
        console.error('[LinkOut] Eligibility check failed; treating as not eligible:', error);
        if (!cancelled) setEligibility(FAIL_CLOSED);
      } finally {
        if (!cancelled) setIsCheckingEligibility(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Mint a fresh hand-off URL. Call this when the disclosure opens, not when it is confirmed.
   */
  const prepareHandoff = useCallback(async (plan: LinkOutPlan) => {
    setIsPreparing(true);
    setPrepareError(null);
    setHandoffUrl(null);

    try {
      const response = await api.post('/api/workflows/subscriptions/handoff', { plan, platform });

      if (response?.success && response.data?.url) {
        setHandoffUrl(response.data.url);
        return response.data.url as string;
      }

      setPrepareError('We could not prepare your checkout link. Please try again.');
      return null;
    } catch (error: any) {
      console.error('[LinkOut] Failed to mint hand-off token:', error);
      setPrepareError(
        error?.status === 429
          ? 'Too many attempts. Please wait a moment and try again.'
          : 'We could not prepare your checkout link. Please try again.'
      );
      return null;
    } finally {
      setIsPreparing(false);
    }
  }, []);

  /** Drop the minted URL. Called when the disclosure closes, so a live token is not left around. */
  const reset = useCallback(() => {
    setHandoffUrl(null);
    setPrepareError(null);
  }, []);

  return {
    eligibility,
    isCheckingEligibility,
    handoffUrl,
    isPreparing,
    prepareError,
    prepareHandoff,
    reset
  };
}
